// Showing prices in the visitor's currency. Customers are always charged in USD; local amounts are
// approximate and use the server's exchange rate (GET /api/pricing/exchange-rate) so every price on a
// page converts at the same rate as the pricing API's `localized` values.
export type LocalRate = { currency: string; symbol: string; rate: number };

const NO_DECIMALS = new Set(['JPY', 'KRW', 'VND', 'IDR', 'HUF', 'CLP', 'COP']);
const thousands = (s: string) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export function formatMoney(amount: number, currency: string, symbol: string): string {
  if (NO_DECIMALS.has(currency)) return `${symbol}${thousands(String(Math.round(amount)))}`;
  if (currency === 'EUR') {
    const [whole, cents] = amount.toFixed(2).split('.');
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${cents} ${symbol}`;
  }
  const [whole, cents] = amount.toFixed(2).split('.');
  return `${symbol}${thousands(whole)}.${cents}`;
}

function usdText(usd: number, wholeDollars?: boolean) {
  return wholeDollars && Number.isInteger(usd) ? `$${thousands(String(usd))}` : formatMoney(usd, 'USD', '$');
}

/**
 * display: the local price on its own ("£1.50"), for prices shown to inform.
 * charge:  the exact USD charge plus the local estimate ("$2.00 (≈ £1.51)"), where money is taken.
 * Until the rate is known, and for US visitors, both are plain dollars.
 */
export function localPriceText(
  usd: number,
  rate: LocalRate | null,
  mode: 'display' | 'charge' = 'display',
  opts: { wholeDollars?: boolean } = {},
): string {
  const dollars = usdText(usd, opts.wholeDollars);
  if (!rate || rate.currency === 'USD' || usd === 0) return dollars;
  const local = formatMoney(usd * rate.rate, rate.currency, rate.symbol);
  return mode === 'charge' ? `${dollars} (≈ ${local})` : local;
}

const USD_RATE: LocalRate = { currency: 'USD', symbol: '$', rate: 1 };

/**
 * The rate a pricing breakdown (the pricing API's `localized`) was computed at. Use it for figures
 * shown next to that breakdown's line items so one summary never mixes two currencies; with no local
 * breakdown, pin dollars rather than falling back to the browser's country.
 */
export function rateFromBreakdown(localized: any): LocalRate {
  const item = localized?.total || localized?.subtotal;
  if (!localized?.exchangeRate || !item?.currency || !item?.symbol) return USD_RATE;
  return { currency: item.currency, symbol: item.symbol, rate: localized.exchangeRate };
}
